import { AlertCircle, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { formatNumber, formatPercent, formatGap, getGapColor } from '../lib/utils';
import { cn } from '../lib/utils';
import { DealAdvisorPanel } from './DealAdvisorPanel';

function getMarginColorVsTarget(pct, target) {
  if (pct < 15) return 'text-red-600';
  if (pct < target) return 'text-orange-500';
  return 'text-green-600';
}

export function RightPanel({
  deal,
  collapsed = false,
  onCollapsedChange,
  advisorOpen,
  onAdvisorOpenChange,
  advisorFocus,
  onAdvisorFocusChange,
  advisorResult,
  advisorLoading,
  advisorError,
  advisorApplyingKey,
  onAdvisorAnalyze,
  onAdvisorApplyCta,
}) {
  if (!deal) {
    return (
      <div className="w-full min-w-0 shrink-0 border-t border-gray-200 bg-gray-50 p-3 flex flex-col lg:border-l lg:border-t-0">
        <p className="text-sm text-gray-500">No deal selected</p>
      </div>
    );
  }

  const verdictClass = {
    GO: 'verdict-go',
    FIX: 'verdict-fix',
    KILL: 'verdict-kill',
  }[deal.verdict] || 'verdict-fix';

  const marginTarget = deal.margin_target_pct || 30;

  const getFixReasonText = () => {
    if (!deal.fix_reasons || deal.fix_reasons.length === 0) return null;
    const reasons = deal.fix_reasons;
    if (reasons.includes('margin') && reasons.includes('budget')) return 'Margin & Budget';
    if (reasons.includes('margin')) return 'Margin below target';
    if (reasons.includes('budget')) return 'Over client budget';
    return null;
  };

  const cc = deal.core_currency || 'CHF';

  return (
    <div className="w-full min-w-0 shrink-0 border-t border-gray-200 bg-gray-50 flex flex-col overflow-hidden lg:border-l lg:border-t-0">
      <div className="border-b border-gray-200 px-2 py-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCollapsedChange?.(!collapsed)}
          className="h-6 w-full justify-between px-1.5 text-[10px] font-medium text-gray-600 hover:text-gray-900"
          data-testid="right-panel-collapse-toggle"
        >
          <span>{collapsed ? 'Expand' : 'Collapse'}</span>
          {collapsed ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {collapsed ? (
        <div className="flex flex-1 flex-col items-center justify-start gap-2 px-1.5 py-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Margin</span>
          <span
            className={cn(
              'text-[18px] font-mono font-bold leading-none tabular-nums',
              getMarginColorVsTarget(deal.total_margin_pct, marginTarget),
            )}
            data-testid="margin-collapsed-percent"
          >
            {formatPercent(deal.total_margin_pct)}
          </span>
          <span className="text-[10px] font-semibold text-gray-500">/ {marginTarget}%</span>
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Verdict */}
        <div 
          className={cn(
            "p-3 border rounded-md flex flex-col gap-1 items-center justify-center text-center",
            verdictClass
          )}
          data-testid="verdict-card"
        >
          <span className="text-xs font-semibold uppercase tracking-wider opacity-70">VERDICT</span>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-2xl font-bold tracking-widest cursor-default" data-testid="verdict-value">
                  {deal.verdict}
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs max-w-52">
                GO = margin on target &amp; within budget. FIX = below target or over budget. KILL = margin below 15%.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {deal.verdict === 'FIX' && getFixReasonText() && (
            <span className="text-xs opacity-80">({getFixReasonText()})</span>
          )}
        </div>

        {/* Margin health */}
        <div className="flex flex-col gap-1">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <label className="panel-label cursor-default">MARGIN</label>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs max-w-52">
                Calculated from {cc} values — the deal's core currency. Independent of display currency.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span 
            className={cn(
              "text-[22px] font-mono font-bold tabular-nums",
              getMarginColorVsTarget(deal.total_margin_pct, marginTarget)
            )}
            data-testid="margin-percent"
          >
            {formatPercent(deal.total_margin_pct)}
          </span>
          {deal.total_margin_pct < marginTarget ? (
            <span className={cn(
              "text-xs font-semibold",
              deal.total_margin_pct < 15 ? "text-red-600" : "text-orange-600"
            )} data-testid="margin-deviation">
              {(marginTarget - deal.total_margin_pct).toFixed(1)}pp below {marginTarget}% target
            </span>
          ) : (
            <span className="text-xs font-semibold text-green-600" data-testid="margin-deviation">
              {(deal.total_margin_pct - marginTarget).toFixed(1)}pp above {marginTarget}% target
            </span>
          )}
        </div>

        <div className="border-t border-gray-200" />

        {/* Gap vs Client Budget */}
        <div className="flex justify-between items-center">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold cursor-default">vs Budget</span>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs max-w-52">
                Revenue minus Client Budget in {cc}. Positive = over budget.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span 
            className={cn("font-mono text-sm font-semibold tabular-nums", getGapColor(deal.gap_vs_client_chf))}
            data-testid="right-gap-client"
          >
            {formatGap(deal.gap_vs_client_chf)} {cc}
          </span>
        </div>

        {/* Gap vs Target */}
        <div className="flex justify-between items-center">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold cursor-default">vs Target</span>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs max-w-52">
                Revenue minus Target Price in {cc}. Shows pricing gap.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span 
            className={cn("font-mono text-sm font-semibold tabular-nums", getGapColor(deal.gap_vs_target_chf))}
            data-testid="right-gap-target"
          >
            {formatGap(deal.gap_vs_target_chf)} {cc}
          </span>
        </div>

        {/* Issues */}
        {deal.verdict !== 'GO' && (
          <>
            <div className="border-t border-gray-200" />
            <div className="flex flex-col gap-2">
              <label className="panel-label">ISSUES</label>
              {deal.total_margin_pct < 15 && (
                <div className="flex items-center gap-2 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Margin below 15% floor
                </div>
              )}
              {deal.total_margin_pct >= 15 && deal.total_margin_pct < marginTarget && (
                <div className="flex items-center gap-2 text-xs text-orange-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Margin below {marginTarget}% target
                </div>
              )}
              {deal.gap_vs_client_chf > 0 && (
                <div className="flex items-center gap-2 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Over client budget by {formatNumber(deal.gap_vs_client_chf)} {cc}
                </div>
              )}
            </div>
          </>
        )}

        {/* AI advisor */}
        {!advisorOpen && (
          <>
            <div className="border-t border-gray-200" />
            <Button
              type="button"
              variant="outline"
              onClick={() => onAdvisorOpenChange(true)}
              className="w-full gap-2 border-violet-300 text-violet-800 hover:bg-violet-50"
              data-testid="deal-advisor-open-btn"
            >
              <Sparkles className="h-4 w-4" />
              AI deal advisor
            </Button>
          </>
        )}

        {advisorOpen && (
          <DealAdvisorPanel
            focus={advisorFocus}
            onFocusChange={onAdvisorFocusChange}
            result={advisorResult}
            loading={advisorLoading}
            error={advisorError}
            onAnalyze={onAdvisorAnalyze}
            onApplyCta={onAdvisorApplyCta}
            applyingKey={advisorApplyingKey}
            onClose={() => onAdvisorOpenChange(false)}
          />
        )}
      </div>
      )}
    </div>
  );
}
